class StickyAddCart extends HTMLElement {
  constructor() {
    super();
    this._vskloPrimaryObserver = null;
    this._vskloLowerObserver = null;
    this._vskloLowerVisible = new Set();
    this._vskloFallbackFrame = 0;
    this._vskloBoundRefreshZones = null;
    this._vskloBoundFallbackUpdate = null;
    this.init();
  }

  init() {
    if (this.dataset.vskloStickyReady === "true") return;
    this.dataset.vskloStickyReady = "true";
    this.setupStickyState();

    const btnMinimal = this.querySelector("minimal-button");
    if (btnMinimal) btnMinimal.addEventListener("click", this.toggle.bind(this), false);
  }

  setupStickyState() {
    this.main = this.closest(".sec__featured-product");
    if (!this.main) return;

    const primaryBtn = this.main.querySelector(".button-trigger__sticky");
    if (!primaryBtn) return;

    this._vskloPrimaryButton = primaryBtn;
    this._vskloPrimaryVisible = true;
    this._vskloLowerZoneSelectors = [
      "footer",
      ".shopify-section-group-footer-group",
      ".footer",
      ".section-apps",
      ".sec__related-product",
      ".related-products",
      'section[id*="related" i]',
      "product-recommendations",
      ".products-recently-viewed",
      "recently-viewed-products",
      'section[id*="recently" i]',
      "#judgeme_product_reviews",
      ".jdgm-widget",
      ".jdgm-review-widget",
      ".jdgm-rev-widg"
    ];

    this._vskloApplyStickyState = () => {
      const lowerZoneVisible = this._vskloLowerVisible.size > 0;
      if (lowerZoneVisible) {
        this.classList.add("is-hidden-before-footer");
        this.classList.remove("show-sticky-cart");
        document.body?.classList.add("vsklo-product-lower-zone");
        return;
      }

      this.classList.remove("is-hidden-before-footer");
      document.body?.classList.remove("vsklo-product-lower-zone");
      this.classList.toggle("show-sticky-cart", !this._vskloPrimaryVisible);
    };

    const getLowerZones = () =>
      Array.from(document.querySelectorAll(this._vskloLowerZoneSelectors.join(","))).filter((node) => {
        if (!node || node === this || node.contains(this)) return false;
        if (node.closest && node.closest(".sec__featured-product")) return false;
        return true;
      });

    if ("IntersectionObserver" in window) {
      this._vskloPrimaryObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this._vskloPrimaryVisible = Boolean(entry && entry.isIntersecting);
        this._vskloApplyStickyState();
      }, { root: null, threshold: 0.01 });
      this._vskloPrimaryObserver.observe(primaryBtn);

      this._vskloLowerObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this._vskloLowerVisible.add(entry.target);
          else this._vskloLowerVisible.delete(entry.target);
        });
        this._vskloApplyStickyState();
      }, {
        root: null,
        rootMargin: "-80px 0px -18% 0px",
        threshold: 0
      });

      this._vskloRefreshLowerZones = () => {
        this._vskloLowerVisible.clear();
        this._vskloLowerObserver.disconnect();
        getLowerZones().forEach((zone) => this._vskloLowerObserver.observe(zone));
        this._vskloApplyStickyState();
      };

      this._vskloBoundRefreshZones = () => this._vskloRefreshLowerZones();
      document.addEventListener("vsklo:content-updated", this._vskloBoundRefreshZones);
      document.addEventListener("shopify:section:load", this._vskloBoundRefreshZones);
      this._vskloRefreshLowerZones();
      return;
    }

    // Legacy fallback only: modern storefronts use IntersectionObserver above.
    this._vskloLowerZones = getLowerZones();
    const updateByRects = () => {
      this._vskloFallbackFrame = 0;
      const buttonRect = primaryBtn.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      this._vskloPrimaryVisible = buttonRect.top < viewportHeight && buttonRect.bottom > 0;
      const hideLine = Math.max(160, Math.min(viewportHeight * 0.82, viewportHeight - 80));
      this._vskloLowerVisible.clear();
      this._vskloLowerZones.forEach((zone) => {
        const rect = zone.getBoundingClientRect();
        if (rect.top < hideLine && rect.bottom > 80) this._vskloLowerVisible.add(zone);
      });
      this._vskloApplyStickyState();
    };
    this._vskloBoundFallbackUpdate = () => {
      if (this._vskloFallbackFrame) return;
      this._vskloFallbackFrame = requestAnimationFrame(updateByRects);
    };
    window.addEventListener("scroll", this._vskloBoundFallbackUpdate, { passive: true });
    window.addEventListener("resize", this._vskloBoundFallbackUpdate, { passive: true });
    this._vskloBoundFallbackUpdate();
  }

  disconnectedCallback() {
    this._vskloPrimaryObserver?.disconnect();
    this._vskloLowerObserver?.disconnect();
    if (this._vskloBoundRefreshZones) {
      document.removeEventListener("vsklo:content-updated", this._vskloBoundRefreshZones);
      document.removeEventListener("shopify:section:load", this._vskloBoundRefreshZones);
    }
    if (this._vskloBoundFallbackUpdate) {
      window.removeEventListener("scroll", this._vskloBoundFallbackUpdate);
      window.removeEventListener("resize", this._vskloBoundFallbackUpdate);
    }
    if (this._vskloFallbackFrame) cancelAnimationFrame(this._vskloFallbackFrame);
    document.body?.classList.remove("vsklo-product-lower-zone");
  }

  toggle(e) {
    const target = e.currentTarget;
    if (!target) return;
    target.classList.toggle("opened");
    const parent = target.closest("sticky-add-cart");
    if (!parent) return;
    const content = parent.querySelector(".content--expand");
    if (!content) return;
    slideAnime({ target: content, animeType: "slideToggle" });
  }
}
if (!customElements.get("sticky-add-cart")) customElements.define("sticky-add-cart", StickyAddCart);



class ProductRecommendations extends SlideSection {
  constructor() {
    super();
  }
  init() {
    this.call();
  }
  disconnectedCallback() {
    if (this._vskloRecommendationObserver) {
      this._vskloRecommendationObserver.disconnect();
      this._vskloRecommendationObserver = null;
    }
    if (typeof super.disconnectedCallback === "function") {
      super.disconnectedCallback();
    }
  }
  call() {
    if (this._vskloRecommendationObserver) return;

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting || !this.isConnected) return;
      observer.unobserve(this);
      this._vskloRecommendationObserver = null;
      fetch(this.dataset.url)
        .then((response) => {
          if (!response.ok) throw new Error("Product recommendations request failed: " + response.status);
          return response.text();
        })
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");
          const markup = recommendations ? recommendations.innerHTML.trim() : "";
          if (markup) {
            this.innerHTML = recommendations.innerHTML;
            document.dispatchEvent(new CustomEvent("vsklo:content-updated", {
              detail: { scope: this, source: "product-recommendations" }
            }));
          } else {
            this.style.display = "none";
            const heading = document.querySelector(".product-recommendations-heading");
            if (heading) heading.remove();
          }
        })
        .finally(() => {
        const hydrateRecommendations = () => {
          if (!this.isConnected) return;
          if (this.querySelector(".swiper-wrapper")) this.initSlide();

          if (
            typeof window.BlsAnimations !== "undefined" &&
            typeof window.BlsAnimations.init === "function"
          ) {
            window.BlsAnimations.init();
          }

          if (
            typeof window.BlsLazyloadImg !== "undefined" &&
            typeof window.BlsLazyloadImg.init === "function"
          ) {
            window.BlsLazyloadImg.init(this);
          }

          if (typeof initLazyloadItem === "function") initLazyloadItem();
        };

        if (this.querySelector(".swiper-wrapper") && typeof window.vskloEnsureSwiper === "function") {
          return window.vskloEnsureSwiper()
            .then(hydrateRecommendations)
            .catch((error) => {
              console.error("[VSKLO] Related-products Swiper load failed:", error);
            });
        }

        hydrateRecommendations();
      })
.catch((e) => {
  console.error(e);
});
    };

    this._vskloRecommendationObserver = new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    });
    this._vskloRecommendationObserver.observe(this);
  }
}
if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}

// js for live view



(function scheduleRecentlyViewedWrite() {
  function run() {
    const prodData = document.querySelector("[data-product-json]");
    if (!prodData) return;

    try {
      const productJson = JSON.parse(prodData.textContent || "{}");
      const currentProductID = String(productJson && productJson.id || "");
      if (!currentProductID) return;

      let storedProducts = [];
      try {
        const parsed = JSON.parse(
          localStorage.getItem("glozin__recently-viewed-products") || "[]"
        );
        if (Array.isArray(parsed)) storedProducts = parsed.map(String);
      } catch (storageParseError) {
        storedProducts = [];
      }

      storedProducts = storedProducts.filter((id) => id !== currentProductID);
      storedProducts.unshift(currentProductID);
      if (storedProducts.length > 25) storedProducts.length = 25;

      localStorage.setItem(
        "glozin__recently-viewed-products",
        JSON.stringify(storedProducts)
      );
    } catch (error) {
      if (window.VSKLO_DEBUG) {
        console.warn("[VSKLO] Recently viewed write skipped:", error);
      }
    }
  }

  function schedule() {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(run, { timeout: 1200 });
    } else {
      window.setTimeout(run, 250);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", schedule, { once: true });
  } else {
    schedule();
  }
})();
var BlsDropDownProduct = (function () {
  let documentClickBound = false;

  function bindSelect(item) {
    if (!item || item.dataset.vskloProductDropdownReady === "true") return;
    const selected = item.querySelector(".select__selected");
    if (!selected) return;

    item.dataset.vskloProductDropdownReady = "true";
    selected.addEventListener("click", (event) => {
      const dropdown = event.currentTarget.nextElementSibling;
      if (dropdown) dropdown.classList.toggle("hidden");
    });
  }

  return {
    innit: function (root = document) {
      const scope = root && root.querySelectorAll ? root : document;
      const controls = [];
      if (scope.matches && scope.matches(".custom-select")) controls.push(scope);
      controls.push(...scope.querySelectorAll(".custom-select"));
      if (!controls.length) return;

      controls.forEach(bindSelect);

      if (!documentClickBound) {
        documentClickBound = true;
        document.addEventListener("click", function (event) {
          if (!event.target.closest(".custom-select")) {
            document.querySelectorAll(".select__dropdown").forEach((item) => {
              item.classList.add("hidden");
            });
          }
        });
      }
    },
  };
})();

BlsDropDownProduct.innit(document);
document.addEventListener("shopify:section:load", function (event) {
  BlsDropDownProduct.innit(event && event.target ? event.target : document);
});
document.addEventListener("vsklo:content-updated", function (event) {
  BlsDropDownProduct.innit(event && event.detail && event.detail.scope ? event.detail.scope : document);
});

var SyncQuantityInput = (() => {
  const selector = ".product-detail__buy-buttons input.quantity-input";

  function bind(input) {
    if (!input || input.dataset.vskloQuantitySyncReady === "true") return;
    input.dataset.vskloQuantitySyncReady = "true";
    input.addEventListener("change", function () {
      const value = this.value;
      document.querySelectorAll(selector).forEach((otherInput) => {
        if (otherInput !== input) otherInput.value = value;
      });
    });
  }

  return {
    init: (root = document) => {
      const scope = root && root.querySelectorAll ? root : document;
      if (scope.matches && scope.matches(selector)) bind(scope);
      scope.querySelectorAll(selector).forEach(bind);
    },
  };
})();

SyncQuantityInput.init(document);
document.addEventListener("shopify:section:load", function (event) {
  SyncQuantityInput.init(event && event.target ? event.target : document);
});
document.addEventListener("vsklo:content-updated", function (event) {
  SyncQuantityInput.init(event && event.detail && event.detail.scope ? event.detail.scope : document);
});

